import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../shared/TailwindComponents/Input';
import Button from '../shared/TailwindComponents/Button';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import FieldShell from '../shared/TailwindComponents/FieldShell';
import { PiTrashLight } from 'react-icons/pi';

interface AddSourceModalContentProps {
  sourceType: 'law' | 'case';
  onConfirm: (data: { sourceData: any; file?: File; files?: File[] }) => void;
  onCancel: () => void;
}

const AddSourceModalContent: React.FC<AddSourceModalContentProps> = ({ sourceType, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [title, setTitle] = useState('');

  const isLaw = sourceType === 'law';

  // Auto-fill title from filename when single file is selected
  useEffect(() => {
    if (files.length === 1 && !title) {
      setTitle(files[0].name.replace(/\.[^/.]+$/, ''));
    } else if (files.length === 0) {
      setTitle('');
    }
  }, [files]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = () => {
    if (files.length === 0) return;

    if (files.length === 1) {
      const file = files[0];
      const sourceTitle = title.trim() || file.name.replace(/\.[^/.]+$/, '');
      onConfirm({ sourceData: { title: sourceTitle }, file });
    } else {
      // Multiple files - pass all files at once
      onConfirm({
        sourceData: { title: '' },
        files,
      });
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const isFormValid = files.length > 0;

  return (
    <div className="md:min-w-[700px] md:max-w-[700px]">
      <div className="space-y-4 px-4">
        {/* Title field - shown for single file uploads */}
        {files.length <= 1 && (
          <div>
            <Input
              id="source-title"
              label={isLaw ? t('Law / Statute Name') : t('Case / Precedent Name')}
              placeholder={isLaw ? t('e.g., Civil Code Section 1234') : t('e.g., Smith v. Jones (2024)')}
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
              type="text"
            />
          </div>
        )}

        <FieldShell
          translate={false}
          label={
            files.length > 1
              ? t('Upload Documents') + ` (${files.length})`
              : t('Upload Document')
          }
        >
          <FileUploader files={files} onFilesChange={setFiles} />
        </FieldShell>

        {/* File list for multi-file uploads */}
        {files.length > 1 && (
          <div className="space-y-1.5 max-h-40 overflow-y-auto custom-scrollbar-main">
            {files.map((file, idx) => (
              <div key={idx} className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-md">
                <span className="text-xs text-gray-700 dark:text-gray-300 truncate flex-1">
                  {file.name}
                </span>
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {(file.size / 1024).toFixed(0)} KB
                </span>
                <button
                  onClick={() => removeFile(idx)}
                  className="text-gray-400 hover:text-danger-500 transition-colors flex-shrink-0"
                >
                  <PiTrashLight className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 mx-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-md border border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
          {isLaw
            ? t('Upload one or more law, statute, or regulation documents. They will be automatically evaluated by AI for relevance to your matter.')
            : t('Upload one or more court ruling or precedent documents. They will be automatically evaluated by AI for relevance to your matter.')
          }
        </p>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-2 px-4">
        <Button
          type="button"
          onClick={onCancel}
          variant="neutral"
          size="small"
        >
          {t('Cancel')}
        </Button>
        <Button
          type="button"
          onClick={handleAdd}
          disabled={!isFormValid}
          variant="primary"
          size="small"
        >
          {files.length > 1
            ? (isLaw ? t('Add Laws') : t('Add Precedents')) + ` (${files.length})`
            : (isLaw ? t('Add Law') : t('Add Precedent'))
          }
        </Button>
      </div>
    </div>
  );
};

export default AddSourceModalContent;
